'use client'

import { cn } from '@/lib/utils'
import { Typo3SectionSpotSanityContentTable } from '@/types'
import { ButtonIcon, Container } from '@/ui/components'
import { useEffect, useRef, useState } from 'react'
import SimpleBar from 'simplebar-react'
import './style.css'

interface Props {
  data: Typo3SectionSpotSanityContentTable
  children?: React.ReactNode
}

const ScrollBar = ({ data, children }: Props) => {
  const c = data.content
  const size = c?.table?.size

  const tableWrapRef = useRef<HTMLDivElement | null>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const checkScrollVisibility = () => {
    if (tableWrapRef.current) {
      const container = tableWrapRef.current
      const maxScrollLeft = container.scrollWidth - container.clientWidth
      const currentScroll = container.scrollLeft

      setCanScrollLeft(currentScroll > 0)
      setCanScrollRight(currentScroll < maxScrollLeft)
    }
  }

  useEffect(() => {
    checkScrollVisibility()
    const handleScroll = () => checkScrollVisibility()

    if (tableWrapRef.current) {
      tableWrapRef.current.addEventListener('scroll', handleScroll)
    }

    return () => {
      if (tableWrapRef.current) {
        // eslint-disable-next-line
        tableWrapRef.current?.removeEventListener('scroll', handleScroll)
      }
    }
  }, [])

  const scrollTable = (direction: 'left' | 'right') => {
    if (tableWrapRef.current) {
      const scrollAmount = 100
      const newScrollLeft =
        direction === 'right'
          ? tableWrapRef.current.scrollLeft + scrollAmount
          : tableWrapRef.current.scrollLeft - scrollAmount

      tableWrapRef.current.scrollTo({ left: newScrollLeft, behavior: 'smooth' })
    }
  }

  const tableWrapClasses = cn(
    'relative',
    size === 'halfsize' && 'lg:max-w-2/3 xl:max-w-1/2'
  )

  return (
    <div className={tableWrapClasses}>
      <SimpleBar autoHide={true} scrollableNodeProps={{ ref: tableWrapRef }}>
        <Container>{children}</Container>
      </SimpleBar>

      <div
        className={cn(
          'gap-2xs right-padding-md absolute bottom-0 z-10 -mb-10 hidden [.simplebar-scrollable-x+&]:flex'
        )}
      >
        <ButtonIcon
          size="m"
          disabled={!canScrollLeft}
          onClick={() => scrollTable('left')}
          icon="material-symbols:arrow-back-ios"
        />
        <ButtonIcon
          size="m"
          disabled={!canScrollRight}
          onClick={() => scrollTable('right')}
          icon="material-symbols:arrow-forward-ios"
        />
      </div>
    </div>
  )
}

export default ScrollBar
